import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";

const LOGO_DARK = "https://customer-assets.emergentagent.com/job_financial-solutions-1/artifacts/hqf4b4fe_2%20-%20Copy.png";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_financial-solutions-1/artifacts/2ymldnhs_1%20-%20Copy.png";

const serviceLinks = [
  { name: "All Services", path: "/services" },
  { name: "Mortgage", path: "/services/mortgage" },
  { name: "Home Insurance", path: "/services/home-insurance" },
  { name: "Protection", path: "/services/protection" },
];

const schemeLinks = [
  { name: "Help To Buy", path: "/schemes/help-to-buy" },
  { name: "Right To Buy", path: "/schemes/right-to-buy" },
  { name: "Right To Acquire", path: "/schemes/right-to-acquire" },
  { name: "Shared Ownership", path: "/schemes/shared-ownership" },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
    setIsServicesOpen(false);
    setIsMobileServicesOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
  ];

  const rightLinks = [
    { name: "Calculator", path: "/calculator" },
    { name: "FAQs", path: "/faqs" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => location.pathname === path;
  const isServicesActive = location.pathname.startsWith("/services") || location.pathname.startsWith("/schemes");

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,0,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-2">
          {/* Logo */}
          <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2">
            <img
              src={isScrolled ? LOGO_LIGHT : LOGO_DARK}
              alt="KK Mortgage Solutions"
              className="h-48 w-auto transition-all duration-300"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  isActive(link.path)
                    ? "bg-[#d64241] text-white"
                    : isScrolled
                    ? "text-gray-700 hover:text-[#d64241] hover:bg-gray-50"
                    : "text-white/90 hover:text-white hover:bg-white/10"
                }`}
              >
                {link.name}
              </Link>
            ))}

            {/* Services Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                data-testid="nav-link-services"
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-1 ${
                  isServicesActive
                    ? "bg-[#d64241] text-white"
                    : isScrolled
                    ? "text-gray-700 hover:text-[#d64241] hover:bg-gray-50"
                    : "text-white/90 hover:text-white hover:bg-white/10"
                }`}
              >
                Services
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isServicesOpen ? "rotate-180" : ""}`} />
              </button>

              {isServicesOpen && (
                <div data-testid="services-dropdown" className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-2 animate-slide-down">
                  <div className="px-3 py-2">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2 mb-1">Services</p>
                    {serviceLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        data-testid={`dropdown-${link.name.toLowerCase().replace(/ /g, '-')}`}
                        className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isActive(link.path) ? "bg-[#fef2f2] text-[#d64241]" : "text-gray-700 hover:bg-gray-50 hover:text-[#d64241]"
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                  <div className="border-t border-gray-100 my-1" />
                  <div className="px-3 py-2">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2 mb-1">Special Schemes</p>
                    {schemeLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        data-testid={`dropdown-${link.name.toLowerCase().replace(/ /g, '-')}`}
                        className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isActive(link.path) ? "bg-[#fef2f2] text-[#d64241]" : "text-gray-700 hover:bg-gray-50 hover:text-[#d64241]"
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {rightLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  isActive(link.path)
                    ? "bg-[#d64241] text-white"
                    : isScrolled
                    ? "text-gray-700 hover:text-[#d64241] hover:bg-gray-50"
                    : "text-white/90 hover:text-white hover:bg-white/10"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Call Now Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:07538962618"
              data-testid="call-now-btn"
              className="inline-flex items-center gap-2 bg-[#d64241] hover:bg-[#b53736] text-white rounded-full px-6 py-2.5 text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 shadow-lg shadow-[#d64241]/20"
            >
              <Phone className="w-4 h-4" />
              Call Now
            </a>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              isScrolled ? "text-black hover:bg-gray-100" : "text-white hover:bg-white/10"
            }`}
          >
            {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-gray-100 animate-slide-down shadow-xl max-h-[80vh] overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            <Link to="/" data-testid="mobile-nav-link-home" className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isActive("/") ? "bg-[#fef2f2] text-[#d64241]" : "text-gray-700 hover:bg-gray-50"}`}>Home</Link>
            <Link to="/about" data-testid="mobile-nav-link-about" className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isActive("/about") ? "bg-[#fef2f2] text-[#d64241]" : "text-gray-700 hover:bg-gray-50"}`}>About</Link>

            {/* Mobile Services Accordion */}
            <button
              data-testid="mobile-services-toggle"
              onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
              className={`flex items-center justify-between w-full px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isServicesActive ? "bg-[#fef2f2] text-[#d64241]" : "text-gray-700 hover:bg-gray-50"}`}
            >
              Services
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isMobileServicesOpen ? "rotate-180" : ""}`} />
            </button>
            {isMobileServicesOpen && (
              <div className="pl-4 space-y-1">
                {serviceLinks.map((link) => (
                  <Link key={link.path} to={link.path} data-testid={`mobile-service-${link.name.toLowerCase().replace(/ /g, '-')}`} className={`block px-4 py-2.5 rounded-lg text-sm transition-colors ${isActive(link.path) ? "text-[#d64241] font-medium" : "text-gray-600 hover:text-[#d64241]"}`}>
                    {link.name}
                  </Link>
                ))}
                <div className="border-t border-gray-100 my-1 ml-4" />
                <p className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase">Schemes</p>
                {schemeLinks.map((link) => (
                  <Link key={link.path} to={link.path} data-testid={`mobile-scheme-${link.name.toLowerCase().replace(/ /g, '-')}`} className={`block px-4 py-2.5 rounded-lg text-sm transition-colors ${isActive(link.path) ? "text-[#d64241] font-medium" : "text-gray-600 hover:text-[#d64241]"}`}>
                    {link.name}
                  </Link>
                ))}
              </div>
            )}

            <Link to="/calculator" data-testid="mobile-nav-link-calculator" className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isActive("/calculator") ? "bg-[#fef2f2] text-[#d64241]" : "text-gray-700 hover:bg-gray-50"}`}>Calculator</Link>
            <Link to="/faqs" data-testid="mobile-nav-link-faqs" className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isActive("/faqs") ? "bg-[#fef2f2] text-[#d64241]" : "text-gray-700 hover:bg-gray-50"}`}>FAQs</Link>
            <Link to="/contact" data-testid="mobile-nav-link-contact" className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isActive("/contact") ? "bg-[#fef2f2] text-[#d64241]" : "text-gray-700 hover:bg-gray-50"}`}>Contact</Link>

            <a href="tel:07538962618" data-testid="mobile-call-btn" className="flex items-center justify-center gap-2 mt-3 bg-[#d64241] text-white rounded-full px-6 py-3 text-sm font-semibold">
              <Phone className="w-4 h-4" />
              Call Now - 07538962618
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
